import { supabase } from '@/integrations/supabase/client';
import { errorFromBody, type Provider } from '@/lib/driveIngestLogic';

export type { Provider } from '@/lib/driveIngestLogic';

/** A folder (or, for Microsoft roots, a drive) returned by the browse actions. */
export interface FolderEntry { id: string; name: string; isFolder?: boolean; driveId?: string }

export interface IngestSourceRow {
  id: string;
  provider: Provider;
  externalFolderId: string;
  driveId: string | null;
  displayPath: string;
  includeSubfolders: boolean;
  enabled: boolean;
  fileTypes: string[];
  lastSyncedAt?: string | null;
  lastStatus?: string | null;
  lastError?: string | null;
}

export interface DriveStatus {
  google: { connected: boolean };
  microsoft: { connected: boolean };
  sources: IngestSourceRow[];
}

// All requests go through the drive-ingest edge function with the user's session;
// the client never sees or sends provider tokens.
async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('drive-ingest', { body });
  if (error) {
    const res = (error as { context?: Response }).context;
    const detail = res && typeof res.json === 'function' ? await res.json().catch(() => null) : null;
    throw errorFromBody(detail, res?.status);
  }
  if (data && typeof data === 'object' && 'error' in data) throw errorFromBody(data);
  return data as T;
}

export const driveIngest = {
  status: () => call<DriveStatus>({ action: 'status' }),
  googleAuthUrl: () => call<{ url: string }>({ action: 'google_auth_url' }),
  googleCallback: (p: { code: string | null; state: string | null; error: string | null }) =>
    call<{ success: true }>({ action: 'google_callback', ...p }),
  roots: (provider: Provider) => call<{ entries: FolderEntry[] }>({ action: 'roots', provider }),
  children: (provider: Provider, parentId: string, driveId?: string) =>
    call<{ entries: FolderEntry[] }>({ action: 'children', provider, parentId, driveId }),
  addSource: (s: {
    provider: Provider; externalFolderId: string; driveId?: string; displayPath: string;
    includeSubfolders: boolean; fileTypes?: string[];
  }) => call<{ source: IngestSourceRow }>({ action: 'source_add', ...s }),
  removeSource: (sourceId: string) => call<{ ok: true }>({ action: 'source_remove', sourceId }),
  syncNow: (provider?: Provider) => call<{ started: true }>({ action: 'sync_now', provider }),
  disconnect: (provider: Provider) => call<{ ok: true }>({ action: 'disconnect', provider }),
};
